import { forwardRef, type InputHTMLAttributes, type JSX, type TextareaHTMLAttributes } from "react";

import { cn } from "@/lib/cn";

/** Field chrome shared by inputs, selects and textareas. Boundary is `line-strong`. */
export const fieldClass =
  "w-full rounded-control border border-control bg-surface px-3 text-base text-ink placeholder:text-ink-subtle transition-colors duration-fast ease-token focus-visible:border-accent focus-visible:outline-none focus-visible:shadow-halo disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-subtle aria-[invalid=true]:border-danger";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** `true` marks the field invalid for assistive technology and paints the border. */
  invalid?: boolean | undefined;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, ...rest },
  ref,
): JSX.Element {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(fieldClass, "h-9", className)}
      {...rest}
    />
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean | undefined;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid = false, rows = 3, ...rest },
  ref,
): JSX.Element {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(fieldClass, "min-h-20 py-2", className)}
      {...rest}
    />
  );
});
